import React, { useState, memo } from 'react';
import styled from 'styled-components';
import { FaBed, FaBath, FaCar } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FavoriteHeart from './FavoriteHeart';
import { useCurrency } from '../../contexts/CurrencyContext';

const Card = styled(Link)`
  display: ${props => props.$viewMode === 'list' ? 'flex' : 'block'};
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: ${props => props.$viewMode === 'list' ? '300px' : '100%'};
  height: ${props => props.$viewMode === 'list' ? '220px' : '200px'};
  flex-shrink: 0;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Content = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0;
`;

const Location = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Features = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;
const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #666;
  font-size: 14px;
`;
const Price = styled.div`
  margin-top: auto;
  font-size: 20px;
  font-weight: 500;
  color: #C4845C;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PriceLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: normal;
`;

const TagsContainer = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 56px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  z-index: 1;
`;

const Tag = styled.span`
  padding: 6px 12px;
  min-width: auto;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const CategoryBadge = styled(Tag)`
  background: ${props => {
    if (props.$category === 'buy' || props.$category === 'comprar') return '#2196F3'; // Blue
    if (props.$category === 'rent' || props.$category === 'arrendar') return '#4CAF50'; // Green
    if (props.$category === 'vacation' || props.$category === 'ferias' || props.$category === 'férias') return '#FF9800'; // Orange
    return '#34574A';
  }};
  color: white;
  min-width: auto;
`;

const HighlightTag = styled(Tag)`
  background-color: #C4845C;
  color: white;
`;


const PropertyCard = ({ property, viewMode = 'grid', isFavorited, onFavorite, onUnfavorite, onClick, onMouseEnter, onMouseLeave }) => {
  const {
    id,
    title,
    location,
    image,
    price,
    quartos,
    banheiros,
    garagem,
    featured,
    searchType = '',
    slug,
    tipoImovel,
    exibirPreco,
  } = property;

  const { t } = useTranslation();
  const { currency, currencySymbol, exchangeRate } = useCurrency();

  const formatPrice = (val, type) => {
    const converted = Math.round(((Number(val) || 0) * exchangeRate + Number.EPSILON) * 100) / 100;
    const formattedPrice = converted.toLocaleString('pt-BR');

    let suffix = '';
    switch ((type || '').toLowerCase()) {
      case 'comprar':
      case 'buy':
      case 'venda':
        suffix = '';
        break;
      case 'arrendar':
      case 'rent':
      case 'aluguel':
        suffix = ' /mês';
        break;
      case 'ferias':
      case 'férias':
      case 'vacation':
        suffix = ' /noite';
        break;
      default:
        suffix = '';
        break;
    }
    return `${currencySymbol || currency} ${formattedPrice}${suffix}`;
  };

  // Heart favorite logic: controlled by props if provided, fallback to local state
  const [localFavorited, setLocalFavorited] = useState(false);
  const favorited = typeof isFavorited === 'boolean' ? isFavorited : localFavorited;

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    if (typeof isFavorited === 'boolean') {
      if (favorited) {
        if (onUnfavorite) onUnfavorite(property);
      } else {
        if (onFavorite) onFavorite(property);
      }
    } else {
      setLocalFavorited(f => !f);
    }
  };

  const buildUrl = () => {
    const base = `/property/${id}`;
    const s = (slug || '').toString().trim();
    return s ? `${base}/${encodeURIComponent(s)}` : base;
  };

  return (
    <Card to={buildUrl()} $viewMode={viewMode} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <ImageContainer $viewMode={viewMode}>
        <FavoriteHeart favorited={favorited} onClick={handleFavoriteClick} />
        <TagsContainer>
          {(tipoImovel || searchType) && (
            <CategoryBadge 
              $category={tipoImovel ? (Number(tipoImovel) === 1 ? 'buy' : Number(tipoImovel) === 2 ? 'rent' : Number(tipoImovel) === 3 ? 'vacation' : '') : searchType}
            >
              {tipoImovel 
                ? (Number(tipoImovel) === 1 ? t('propertyCard.category.buy') : Number(tipoImovel) === 2 ? t('propertyCard.category.rent') : Number(tipoImovel) === 3 ? t('propertyCard.category.vacation') : '')
                : (searchType === 'comprar' || searchType === 'buy' || searchType === 'venda' ? t('propertyCard.category.buy') : searchType === 'arrendar' || searchType === 'rent' || searchType === 'aluguel' ? t('propertyCard.category.rent') : searchType === 'ferias' || searchType === 'férias' || searchType === 'vacation' ? t('propertyCard.category.vacation') : '')
              }
            </CategoryBadge>
          )}
          {featured && (
            <HighlightTag>Destaque</HighlightTag>
          )}

        </TagsContainer>
        <Image src={image} alt={title} />
      </ImageContainer>

      <Content>
        <Title>{title}</Title>
        <Location>{location}</Location>
        <Features>
          <Feature>
            <FaBed />
            {quartos} {t('propertyCard.bedrooms')}
          </Feature>
          <Feature>
            <FaBath />
            {banheiros} {Number(banheiros) === 1 ? t('propertyCard.bathrooms.singular') : t('propertyCard.bathrooms.plural')}
          </Feature>
          <Feature>
            <FaCar />
            {garagem} {Number(garagem) === 1 ? t('propertyCard.parking.singular') : t('propertyCard.parking.plural')}
          </Feature>
        </Features>

        <Price>
          {exibirPreco === false || exibirPreco === 0 || exibirPreco === '0' ? (
            <>
              <div style={{ fontSize: '16px', fontWeight: '500', color: '#C4845C', whiteSpace: 'nowrap' }}>Sob consulta</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Entre em contato com o proprietário para mais informações sobre o valor deste imóvel.
              </div>
            </>
          ) : (
            formatPrice(price, searchType)
          )}
        </Price>
      </Content>
    </Card>
  );
};

export default memo(PropertyCard);